// 手机端让表单、记录与操作自然换行，移除固定最小宽度；业务提交与原有权限保持一致。
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { ClipboardCheck, ExternalLink, Loader2, Paperclip } from "lucide-react";
import { fmtD } from "../lib/date";

export default function DoneReport() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null);
    setErr("");
    api.get("/engine-trace/done-report-detail", { params: { date } })
      .then(({ data: d }) => setData(d))
      .catch((e) => setErr(e?.detailText || (zh ? "加载失败（仅管理层可查看）" : "Load failed (managers only)")));
  }, [date]); // eslint-disable-line

  return (
    <div className="space-y-5 max-w-4xl" data-testid="done-report-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-emerald-600" />{zh ? "当日完成明细" : "Daily Completion Detail"}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{zh ? "各项目当天完成的必办事项 · 含完成人/时间/留言/凭证数" : "Tasks completed per project with who/when/note/evidence"}</p>
        </div>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
          className="text-xs rounded-lg border border-slate-200 px-2.5 py-1.5 bg-white" data-testid="done-report-date" />
      </div>

      {err && <div className="bg-white rounded-2xl border border-red-100 p-8 text-center text-sm text-red-500" data-testid="done-report-error">{err}</div>}
      {!err && !data && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>}

      {data && (
        <>
          <div className="bg-emerald-600 text-white rounded-2xl p-4 flex flex-wrap items-center gap-6" data-testid="done-report-summary">
            <div><p className="text-2xl font-black">{data.total}</p><p className="text-[11px] text-emerald-100">{zh ? "完成事项" : "Tasks done"}</p></div>
            <div><p className="text-2xl font-black">{data.projects}</p><p className="text-[11px] text-emerald-100">{zh ? "覆盖项目" : "Projects"}</p></div>
            <p className="ml-auto text-xs text-emerald-100">🏁 {fmtD(data.date)}</p>
          </div>

          {data.total === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 text-sm" data-testid="done-report-empty">
              {zh ? "该日期没有完成记录" : "No completions on this date"}
            </div>
          )}

          {data.groups.map((g) => (
            <div key={g.projectId} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`done-report-proj-${g.projectId}`}>
              <button onClick={() => nav(`/project/${g.projectId}`)} className="text-sm font-bold text-slate-800 hover:text-indigo-600 flex flex-wrap text-left items-center gap-1.5 mb-2">
                {g.address}<span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{g.count} {zh ? "项" : ""}</span>
                {g.manager && <span className="text-[10px] font-normal text-slate-400">PM · {g.manager}</span>}
                <ExternalLink className="h-3.5 w-3.5 text-slate-300" />
              </button>
              <div className="space-y-1">
                {g.tasks.map((t) => (
                  <div key={t.taskId} className="flex items-center gap-2.5 rounded-xl bg-slate-50/60 border border-slate-100 px-3 py-2" data-testid={`done-report-task-${t.taskId}`}>
                    <span className="text-emerald-500 shrink-0">✓</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-slate-700 break-words sm:truncate" title={t.title}>{t.title}{t.custom && <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-red-50 text-red-500">{zh ? "自建" : "custom"}</span>}</p>
                      <p className="text-[10px] text-slate-400 break-words sm:truncate">{t.subName || ""}{t.doneNote ? ` · 💬 ${t.doneNote}` : ""}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[11px] font-semibold text-slate-600" data-testid={`done-report-by-${t.taskId}`}>{t.doneBy || "-"}</p>
                      <p className="text-[10px] text-slate-400 flex items-center justify-end gap-1">
                        {t.doneAt ? String(t.doneAt).slice(11, 16) : ""}
                        {t.evidenceCount > 0 && <span className="inline-flex items-center gap-0.5 text-indigo-500"><Paperclip className="h-2.5 w-2.5" />{t.evidenceCount}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
